import { useIntl } from 'react-intl';
import { capDoohReportOta } from 'sonata-static-storage/lib/helpers/doohReport.helpers';
import { formatNumber, formatPercentage } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';
import { normalizeWithUnitAndThousandAndMillions } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { formatMoney as formatMoneyWithCurrency } from '@/components/driveToStore/common/helpers/formatters';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';

const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

/**
 * Value formatters shared across the DOOH report, localized to the user's base
 * currency. Every formatter except `formatMoney` renders "N/A" for a missing
 * value; `formatMoney` keeps the shared money formatter's own handling.
 */
export function useDoohReportFormatters() {
  const { formatMessage, formatDate } = useIntl();
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const formatCountry = baseCurrency?.countries?.[0] || 'default';
  const notAvailable = formatMessage({ defaultMessage: 'N/A', id: 'doohReport.metrics.notAvailable' });

  const formatCount = (value: number | null): string =>
    value === null ? notAvailable : formatNumber(value, { country: formatCountry, fixed: 0 });

  const formatDecimal = (value: number | null, fixedDecimals: number): string =>
    value === null ? notAvailable : formatNumber(value, { country: formatCountry, fixed: fixedDecimals });

  /**
   * OTA (or event lift) as a multiplier, e.g. "x1.5". Above the report's display cap it reads
   * "> x10" instead of the exact figure (see `capDoohReportOta`).
   */
  const formatOta = (value: number | null): string => {
    const shown = capDoohReportOta(value);
    if (!shown) {
      return notAvailable;
    }
    return shown.capped ? `> x${formatDecimal(shown.value, 0)}` : `x${formatDecimal(shown.value, 1)}`;
  };

  const formatAbbreviated = (value: number | null): string =>
    value === null
      ? notAvailable
      : normalizeWithUnitAndThousandAndMillions(value, { countries: baseCurrency?.countries, currencySymbol: '' });

  const formatPercent = (value: number | null, fixedDecimals = 0): string =>
    value === null ? notAvailable : formatPercentage(value, { base: 100, country: formatCountry, fixedDecimals });

  const formatSignedPercent = (value: number | null, fixedDecimals = 0): string =>
    value === null ? notAvailable : `${value > 0 ? '+' : ''}${formatPercent(value, fixedDecimals)}`;

  const formatMoney = (value: number | null): string => formatMoneyWithCurrency(value, baseCurrency);

  const formatShortDate = (date: string | null): string => {
    const parsed = date ? new Date(date.replace(' ', 'T')) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? formatDate(parsed, SHORT_DATE_OPTIONS) : notAvailable;
  };

  return {
    formatAbbreviated,
    formatCount,
    formatDecimal,
    formatMoney,
    formatOta,
    formatPercent,
    formatShortDate,
    formatSignedPercent,
    notAvailable
  };
}
